import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiFolder, FiRefreshCw, FiSearch, FiX } from "react-icons/fi";
import { client } from "../../Clients";
import type { APAADMINBaseModelsPageTreeNode } from "../../API/Models";
import PageTreeRow from "./PageTreeRow";
import PageTreeSearchResults from "./PageTreeSearchResults";

type BranchState = {
	folders: APAADMINBaseModelsPageTreeNode[];
	pages: APAADMINBaseModelsPageTreeNode[];
	loading: boolean;
	loaded: boolean;
	error: string | null;
};

const normalizePath = (value?: string | null) =>
	value?.trim().replace(/^\/+|\/+$/g, "").toLowerCase() ?? "";
const ROOT_SLUG_ALIASES = new Set(["home", "accueil", "index"]);

type PageTreePanelProps = {
	language: string;
	activePageId?: string | null;
	activePageSlug?: string | null;
	activeFolderPath?: string | null;
	defaultExpandedPaths?: string[];
	onSelectPage: (node: APAADMINBaseModelsPageTreeNode) => void;
	onSelectFolder?: (path: string | null) => void;
	className?: string;
};

/** Page tree panel with folder navigation, search and async loading. */
const PageTreePanel: React.FC<PageTreePanelProps> = ({
	language,
	activePageId,
	activePageSlug,
	activeFolderPath,
	defaultExpandedPaths,
	onSelectPage,
	onSelectFolder,
	className
}) => {
	const [branches, setBranches] = useState<Record<string, BranchState>>({});
	const branchesRef = useRef<Record<string, BranchState>>({});
	const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
	const [search, setSearch] = useState("");
	const [searchLoading, setSearchLoading] = useState(false);
	const [searchError, setSearchError] = useState<string | null>(null);
	const [searchResults, setSearchResults] = useState<APAADMINBaseModelsPageTreeNode[]>([]);

	const normalizedActiveSlug = useMemo(() => normalizePath(activePageSlug), [activePageSlug]);
	const normalizedFolder = useMemo(() => normalizePath(activeFolderPath), [activeFolderPath]);
	const normalizedLanguage = useMemo(() => language?.trim().toLowerCase() ?? "fr", [language]);

	const loadBranch = useCallback(
		async (path?: string | null, force = false) => {
			const normalized = normalizePath(path);
			const current = branchesRef.current[normalized];
			if (!force && (current?.loading || current?.loaded)) {
				return;
			}

			setBranches((prev) => ({
				...prev,
				[normalized]: {
					folders: current?.folders ?? [],
					pages: current?.pages ?? [],
					loading: true,
					loaded: current?.loaded ?? false,
					error: null
				}
			}));

			try {
				const response = await client.content.apiContentPageTreeGet(
					normalizedLanguage,
					normalized || undefined
				);
				const data = response.data;
				setBranches((prev) => ({
					...prev,
					[normalized]: {
						folders: data?.folders ?? [],
						pages: data?.pages ?? [],
						loading: false,
						loaded: true,
						error: null
					}
				}));
			} catch (err) {
				console.error(err);
				setBranches((prev) => ({
					...prev,
					[normalized]: {
						folders: current?.folders ?? [],
						pages: current?.pages ?? [],
						loading: false,
						loaded: true,
						error: "Unable to load folders."
					}
				}));
			}
		},
		[normalizedLanguage]
	);

	useEffect(() => {
		branchesRef.current = branches;
	}, [branches]);

	useEffect(() => {
		setBranches({});
		setExpandedPaths(new Set());
		setSearch("");
		setSearchResults([]);
		setSearchError(null);
		loadBranch(null, true);
	}, [normalizedLanguage, loadBranch]);

	useEffect(() => {
		if (!defaultExpandedPaths?.length) return;
		const normalizedDefaults = defaultExpandedPaths.map(normalizePath).filter(Boolean);
		if (normalizedDefaults.length === 0) return;
		setExpandedPaths(new Set(normalizedDefaults));
		// Force load so we always fetch children when expanding to the current page (avoids stale empty cache)
		normalizedDefaults.forEach((path) => loadBranch(path, true));
	}, [defaultExpandedPaths, loadBranch]);

	useEffect(() => {
		const term = search.trim();
		if (!term) {
			setSearchResults([]);
			setSearchError(null);
			setSearchLoading(false);
			return;
		}

		setSearchLoading(true);
		setSearchError(null);
		const handle = window.setTimeout(async () => {
			try {
				const response = await client.content.apiContentPageTreeSearchGet(
					normalizedLanguage,
					term,
					100
				);
				setSearchResults(response.data?.results ?? []);
			} catch (err) {
				console.error(err);
				setSearchError("Unable to search pages.");
				setSearchResults([]);
			} finally {
				setSearchLoading(false);
			}
		}, 300);

		return () => window.clearTimeout(handle);
	}, [search, normalizedLanguage]);

	const isPageActive = (node: APAADMINBaseModelsPageTreeNode) => {
		const nodeSlug = normalizePath(node.slug ?? node.path);
		return (!!activePageId && node.id === activePageId) || (!!normalizedActiveSlug && nodeSlug === normalizedActiveSlug);
	};

	const renderBranch = (path: string | null, depth: number) => {
		const normalized = normalizePath(path);
		const branch = branches[normalized];
		if (!branch?.loaded && branch?.loading) {
			return (
				<div className="text-xs text-slate-400" style={{ paddingLeft: depth * 16 + 12 }}>
					Loading…
				</div>
			);
		}

		if (branch?.error) {
			return (
				<div className="text-xs text-rose-500" style={{ paddingLeft: depth * 16 + 12 }}>
					{branch.error}
				</div>
			);
		}

		const folders = [...(branch?.folders ?? [])];
		const pages = [...(branch?.pages ?? [])];
		const cleanSortLabel = (value: string) =>
			value.replace(/[\/]+/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
		const getFolderLabel = (node: APAADMINBaseModelsPageTreeNode) => (node.name || node.path || "").trim();
		const getPageLabel = (node: APAADMINBaseModelsPageTreeNode) => (node.title || node.name || "").trim();
		/** Sort key for pages: slug first (then path/name); folders by name/path. */
		const getSortKey = (type: "folder" | "page", node: APAADMINBaseModelsPageTreeNode) =>
			type === "folder" ? getFolderLabel(node) : (node.slug || node.path || node.name || "").trim();
		const items = [
			...folders.map((node) => ({ type: "folder" as const, node })),
			...pages.map((node) => ({ type: "page" as const, node }))
		].sort((a, b) => {
			const aKey = getSortKey(a.type, a.node);
			const bKey = getSortKey(b.type, b.node);
			const diff = cleanSortLabel(aKey).localeCompare(cleanSortLabel(bKey), undefined, { sensitivity: "base" });
			if (diff !== 0) return diff;
			if (a.type === b.type) return 0;
			return a.type === "folder" ? -1 : 1;
		});
		if (branch?.loaded && items.length === 0) {
			return (
				<div className="text-xs text-slate-400" style={{ paddingLeft: depth * 16 + 12 }}>
					No pages in this folder.
				</div>
			);
		}

		return (
			<>
				{items.map(({ type, node }) => {
					if (type === "folder") {
						const folder = node;
						const folderPath = normalizePath(folder.path ?? folder.slug ?? folder.name);
						const isExpanded = expandedPaths.has(folderPath);
						const isActive = !!normalizedFolder && folderPath === normalizedFolder;

						return (
							<React.Fragment key={`folder-${folder.path ?? folder.name}`}>
								<PageTreeRow
									node={folder}
									depth={depth}
									variant="folder"
									isExpanded={isExpanded}
									isActive={isActive}
									onToggle={
										folder.hasChildren
											? () => {
													setExpandedPaths((prev) => {
														const next = new Set(prev);
														if (next.has(folderPath)) {
															next.delete(folderPath);
														} else {
															next.add(folderPath);
														}
														return next;
													});
													if (!isExpanded) {
														loadBranch(folderPath);
													}
											  }
											: undefined
									}
									onSelect={() => {
										onSelectFolder?.(folderPath);
										if (!expandedPaths.has(folderPath)) {
											setExpandedPaths((prev) => new Set(prev).add(folderPath));
											if (folder.hasChildren) {
												loadBranch(folderPath);
											}
										}
									}}
								/>
								{isExpanded && renderBranch(folderPath, depth + 1)}
							</React.Fragment>
						);
					}

					const page = node;
					const slugValue = normalizePath(page.slug ?? page.path);
					const isRootPage =
						depth === 0 &&
						(!slugValue || (!slugValue.includes("/") && ROOT_SLUG_ALIASES.has(slugValue)));
					return (
						<PageTreeRow
							key={`page-${page.id ?? page.slug ?? page.path}`}
							node={page}
							depth={depth}
							variant="page"
							isActive={isPageActive(page)}
							isRootPage={isRootPage}
							onSelect={() => onSelectPage(page)}
						/>
					);
				})}
			</>
		);
	};

	const handleRefresh = useCallback(() => {
		loadBranch(null, true);
		expandedPaths.forEach((path) => loadBranch(path, true));
	}, [expandedPaths, loadBranch]);

	return (
		<div className={className}>
			<div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-4 h-full min-h-0 flex flex-col">
				<div className="flex items-center justify-between">
					<div>
						<p className="text-xs uppercase tracking-[0.3em] text-slate-400">Pages</p>
						<p className="text-sm text-slate-600">Navigate the page tree</p>
					</div>
					<button
						type="button"
						onClick={handleRefresh}
						className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
						title="Refresh tree"
					>
						<FiRefreshCw className="mr-1 h-3 w-3" />
						Refresh
					</button>
				</div>

				<div className="relative">
					<FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
					<input
						className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-10 text-sm focus:border-slate-400 focus:outline-none"
						placeholder="Search pages"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
					{search && (
						<button
							type="button"
							onClick={() => setSearch("")}
							className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
							aria-label="Clear search"
						>
							<FiX className="h-4 w-4" />
						</button>
					)}
				</div>

				<div className="flex-1 min-h-0 overflow-auto pr-1">
					{search.trim() ? (
						<PageTreeSearchResults
							loading={searchLoading}
							error={searchError}
							results={searchResults}
							onSelectPage={onSelectPage}
						/>
					) : (
						<div className="space-y-1">{renderBranch(null, 0)}</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default PageTreePanel;
